import axios from 'axios';
import { checkApiVersionHeader } from 'src/api';
import { decodeBlobErrorBody } from 'src/api/utils/errorUtils';
import {
  ApplogsApiClient,
  AttachmentApiClient,
  AzureGroupSyncApiClient,
  BackgroundJobsApiClient,
  ChangeApiClient,
  ChangesOverviewApiClient,
  ChangeTemplateApiClient,
  ChangeTypeApiClient,
  ChatbotApiClient,
  ChatMemoryApiClient,
  ClassificationApiClient,
  ClientApiClient,
  ContactApiClient,
  CTCAdjustmentApiClient,
  CTCApiClient,
  DailyApiClient,
  DailyProgressApiClient,
  DailyQuestionApiClient,
  DailySettingApiClient,
  DefaultFolderApiClient,
  DesignModelApiClient,
  DesignProgressApiClient,
  DocumentApiClient,
  DrawingApiClient,
  EmailApiClient,
  HealthCheckApiClient,
  HyperlinkApiClient,
  InspectionsOverviewApiClient,
  JobApiClient,
  KnowledgeArticleApiClient,
  ManhoursApiClient,
  MeetingApiClient,
  MeetingCommentApiClient,
  MeetingIssueApiClient,
  MeetingTopicApiClient,
  MeetingTypeApiClient,
  MilestonePhaseApiClient,
  MilestonePhaseItemApiClient,
  MilestoneTemplateApiClient,
  NotificationsApiClient,
  OfficeApiClient,
  OrganizationApiClient,
  OrganizationRoleApiClient,
  PayCodeApiClient,
  PostApiClient,
  RateApiClient,
  RfiApiClient,
  RfisOverviewApiClient,
  ServiceJobApiClient,
  ServiceWorkOrderApiClient,
  SubmittalApiClient,
  TimesheetApiClient,
  TransmittalApiClient,
  UnitPricingApiClient,
  UserApiClient,
  UserNotificationApiClient,
  UserProfileApiClient,
  UserRateApiClient,
  WbsApiClient,
  WorkflowsApiClient,
  ScimIntegrationApiClient,
  ApiConnectionApiClient,
  ProactSettingApiClient,
  WorkTicketApiClient,
} from 'src/api/FiretrolApiClient';
import { apiOrigin } from 'src/api/apiOrigin';
import { getAuthOptions } from 'src/api/helpers';
import { triggerDownload } from 'src/utils';

const AxiosInstance = axios.create({
  transformResponse: [],
});

const url = apiOrigin;
AxiosInstance.interceptors.request.use(
  async (config) => {
    const authOptions = await getAuthOptions();
    if (authOptions) {
      // Only set Content-Type if not FormData (let Axios auto-set multipart/form-data with boundary)
      if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = authOptions.headers['Content-Type'];
      }
      config.headers.Authorization = `Bearer ${authOptions.headers.Authorization}`;

      if (authOptions.headers['X-Assume-User-Id']) {
        config.headers['X-Assume-User-Id'] =
          authOptions.headers['X-Assume-User-Id'];
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Check for API version changes on every response
AxiosInstance.interceptors.response.use(
  (response) => {
    checkApiVersionHeader(response.headers as Record<string, string>);
    return response;
  },
  async (error) => {
    return Promise.reject(await decodeBlobErrorBody(error));
  },
);

export const changesOverviewApiClient = new ChangesOverviewApiClient(
  url,
  AxiosInstance,
);
export const rfisOverviewApiClient = new RfisOverviewApiClient(
  url,
  AxiosInstance,
);
export const inspectionsOverviewApiClient = new InspectionsOverviewApiClient(
  url,
  AxiosInstance,
);
export const ctcApiClient = new CTCApiClient(url, AxiosInstance);
export const ctcAdjustmentApiClient = new CTCAdjustmentApiClient(
  url,
  AxiosInstance,
);
export const healthCheckApiClient = new HealthCheckApiClient(
  url,
  AxiosInstance,
);
export const hyperlinkApiClient = new HyperlinkApiClient(url, AxiosInstance);
export const rateApiClient = new RateApiClient(url, AxiosInstance);
export const timesheetApiClient = new TimesheetApiClient(url, AxiosInstance);
export const unitPricingApiClient = new UnitPricingApiClient(
  url,
  AxiosInstance,
);
export const manhoursApiClient = new ManhoursApiClient(url, AxiosInstance);
export const workflowApiClient = new WorkflowsApiClient(url, AxiosInstance);
export const organizationApiClient = new OrganizationApiClient(
  url,
  AxiosInstance,
);
export const userApiClient = new UserApiClient(url, AxiosInstance);
export const userProfileApiClient = new UserProfileApiClient(
  url,
  AxiosInstance,
);
export const designModelApiClient = new DesignModelApiClient(
  url,
  AxiosInstance,
);
export const contactApiClient = new ContactApiClient(url, AxiosInstance);
export const workTicketApiClient = new WorkTicketApiClient(url, AxiosInstance);
export const dailyApiClient = new DailyApiClient(url, AxiosInstance);
export const dailyProgressApiClient = new DailyProgressApiClient(
  url,
  AxiosInstance,
);
export const dailyQuestionApiClient = new DailyQuestionApiClient(
  url,
  AxiosInstance,
);
export const payCodeApiClient = new PayCodeApiClient(url, AxiosInstance);
export const classificationApiClient = new ClassificationApiClient(
  url,
  AxiosInstance,
);
export const userRateApiClient = new UserRateApiClient(url, AxiosInstance);
export const emailApiClient = new EmailApiClient(url, AxiosInstance);
export const defaultFolderApiClient = new DefaultFolderApiClient(
  url,
  AxiosInstance,
);
export const milestoneTemplateApiClient = new MilestoneTemplateApiClient(
  url,
  AxiosInstance,
);
export const milestonePhaseApiClient = new MilestonePhaseApiClient(
  url,
  AxiosInstance,
);
export const milestonePhaseItemApiClient = new MilestonePhaseItemApiClient(
  url,
  AxiosInstance,
);
export const userNotificationApiClient = new UserNotificationApiClient(
  url,
  AxiosInstance,
);
export const dailySettingApiClient = new DailySettingApiClient(
  url,
  AxiosInstance,
);
export const organizationRoleApiClient = new OrganizationRoleApiClient(
  url,
  AxiosInstance,
);
export const clientApiClient = new ClientApiClient(url, AxiosInstance);
export const officeApiClient = new OfficeApiClient(url, AxiosInstance);
export const serviceJobApiClient = new ServiceJobApiClient(url, AxiosInstance);
export const serviceWorkOrderApiClient = new ServiceWorkOrderApiClient(
  url,
  AxiosInstance,
);
export const submittalApiClient = new SubmittalApiClient(url, AxiosInstance);
export const transmittalApiClient = new TransmittalApiClient(
  url,
  AxiosInstance,
);
export const meetingApiClient = new MeetingApiClient(url, AxiosInstance);
export const meetingTypeApiClient = new MeetingTypeApiClient(
  url,
  AxiosInstance,
);
export const meetingTopicApiClient = new MeetingTopicApiClient(
  url,
  AxiosInstance,
);
export const meetingIssueApiClient = new MeetingIssueApiClient(
  url,
  AxiosInstance,
);
export const meetingCommentApiClient = new MeetingCommentApiClient(
  url,
  AxiosInstance,
);
export const rfiApiClient = new RfiApiClient(url, AxiosInstance);
export const jobApiClient = new JobApiClient(url, AxiosInstance);
export const knowledgeArticleApiClient = new KnowledgeArticleApiClient(
  url,
  AxiosInstance,
);
export const wbsApiClient = new WbsApiClient(url, AxiosInstance);
export const attachmentApiClient = new AttachmentApiClient(url, AxiosInstance);
export const designProgressApiClient = new DesignProgressApiClient(
  url,
  AxiosInstance,
);
export const documentApiClient = new DocumentApiClient(url, AxiosInstance);
export const changeApiClient = new ChangeApiClient(url, AxiosInstance);
export const changeTemplateApiClient = new ChangeTemplateApiClient(
  url,
  AxiosInstance,
);
export const changeTypeApiClient = new ChangeTypeApiClient(url, AxiosInstance);
export const drawingApiClient = new DrawingApiClient(url, AxiosInstance);
export const postApiClient = new PostApiClient(url, AxiosInstance);
export const apiConnectionApiClient = new ApiConnectionApiClient(
  url,
  AxiosInstance,
);
export const proactSettingApiClient = new ProactSettingApiClient(
  url,
  AxiosInstance,
);
export const scimIntegrationApiClient = new ScimIntegrationApiClient(
  url,
  AxiosInstance,
);
export const azureGroupSyncApiClient = new AzureGroupSyncApiClient(
  url,
  AxiosInstance,
);
export const backgroundJobsApiClient = new BackgroundJobsApiClient(
  url,
  AxiosInstance,
);
export const chatbotApiClient = new ChatbotApiClient(url, AxiosInstance);
export const chatMemoryApiClient = new ChatMemoryApiClient(url, AxiosInstance);
export const applogsApiClient = new ApplogsApiClient(url, AxiosInstance);
export const notificationsApiClient = new NotificationsApiClient(
  url,
  AxiosInstance,
);

// Notification DTOs are re-exported here so hooks/components can consume them
// without importing the generated client directly (api-client containment).
export type {
  NotificationPreviewDto,
  NotificationHistoryDto,
  NotificationCompositionRequest,
  RecipientDto as NotificationRecipientDto,
  UserDto,
  UserSearchDto,
  ResourceAttachmentDto,
  WorkflowDto,
  ChangeDocumentOptionDto,
  RfiDocumentOptionDto,
  SubmittalDocumentOptionDto,
  ChangeTypeDto,
  ChangeTypeRequestDto,
  TransmittalDto,
  TransmittalPdfOptionDto,
  TransmittalPdfLinkDto,
  PostDto,
  PostDocumentDto,
  AttachmentDto,
  PostPaginationMeta,
  PostFilterDto,
  DocumentDto,
  DocumentSharedWithDto,
  DocumentVersionDto,
  FolderRequestDto,
  SharedWithUserRequest,
  FileParameter,
  JobUserDto,
  DefaultFolderDto,
  FolderTemplateDto,
} from 'src/api/FiretrolApiClient';

/**
 * Helper function to download a document and trigger the browser download.
 * This wraps the documentApiClient.downloadDocument call for convenience.
 */
export const downloadDocument = async (
  jobId: number,
  documentId: string,
): Promise<void> => {
  const result = await documentApiClient.downloadDocument(jobId, documentId);
  triggerDownload(result.url, result.name);
};
